import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// components
import Grid from '@mui/material/Grid2'
import { ReduxSwitch } from 'components/FormElements'
// view components
import Text from '../Text'
import Lookup from '../Lookup'
import MatrixField from '../MatrixField'
import RecordLinks from '../RecordLinks'
import ParentFields from '../ParentFields'
import DocumentOrLink from 'components/FormElements/DocumentOrLink/Risk'
import ConditionButton from '../RiskTemplateField/components/ConditionButton'
// constants
import { Forms } from 'constants/forms'
// hooks
import { useFieldType } from 'hooks/risks'
import { useNetworkStatus } from 'hooks/useNetworkStatus'
// types
import { FieldType, IField } from 'types/graphql'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { ImportedRiskContext } from 'components/Modals/ImportedRiskModal'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { generateFields } from 'utils/fields'

interface Props {
  name: string
  field: IField
  form?: string
  isForm?: boolean
  disabled?: boolean
  canModifyForm?: boolean
}

const onlineFields = [FieldType.Lookup, FieldType.Address]

const FieldContent: React.FC<Props> = ({
  name,
  field,
  isForm,
  disabled,
  canModifyForm,
  form = Forms.RISK_TEMPLATE_FORM
}) => {
  const router = useRouter()
  const onLine = useNetworkStatus()

  const { risk } = React.useContext(RiskAPIContext)
  const { riskTemplate, isRiskTemplate, isRisksRegister } = React.useContext(RiskTemplateAPIContext)
  const { isImportedField, canModifyImportedFields, importedRisk } = React.useContext(ImportedRiskContext)

  const { isText, isNumber, isSwitch, isMatrix, isDocOrLink, isLookup, isRecordLinks, isParentFields } =
    useFieldType(field)

  const currentRisk = importedRisk || risk

  const canModifyRisk = currentRisk?.permissions.riskModify && currentRisk?.canUpdateFromSystemProcesses
  const canModifyOnOffline = !onLine && !field.readonly && !isRisksRegister && !onlineFields.includes(field.fieldType)
  const canModifyField = isRiskTemplate || (isForm ? canModifyForm : canModifyRisk) || canModifyImportedFields
  const canModifyReadOnly = isRiskTemplate || !field.readonly || currentRisk?.permissions.editReadOnlyFields
  const canModifyOnRegister = !router.query.multiple && (!isRisksRegister || isImportedField)

  const canModify =
    !disabled &&
    (canModifyOnOffline || (!riskTemplate.archived && canModifyReadOnly && canModifyOnRegister && canModifyField))

  const isTableField = !!riskTemplate.fields.find(
    (f) => f.id === field.parentFieldId && f.fieldType === FieldType.Table
  )
  const showCondition = field.parentFieldId ? !isTableField : true

  const canSetPermissions = isRiskTemplate
    ? riskTemplate.permissions?.riskSetUserRiskSecurity
    : currentRisk?.permissions?.riskSetUserRiskSecurity

  if (isText || isNumber) {
    return <Text name={name} field={field} disabled={!canModify} />
  }

  if (isMatrix) {
    return <MatrixField isDisabled={!canModify} field={field} name={name} required={field.required} />
  }

  if (isDocOrLink) {
    return <DocumentOrLink form={form} isDisabled={!canModify} field={field} name={name} required={field.required} />
  }

  if (isSwitch) {
    return (
      <Grid key={field.id} className='switch-wrapper' data-testid='risk-field-value' data-entity-id={field.id}>
        <ReduxSwitch name={name} label={field.label} disabled={!canModify} required={field.required} />
        {isRiskTemplate && showCondition && <ConditionButton value={true} fieldId={field.id} />}
      </Grid>
    )
  }

  if (isLookup) {
    return (
      <Lookup
        name={name}
        form={form}
        field={field}
        disabled={!canModify}
        isTableField={isTableField}
        canSetPermissions={canSetPermissions}
      />
    )
  }

  if (isRecordLinks) {
    return <RecordLinks field={field} />
  }

  if (isParentFields) {
    return <ParentFields field={field} />
  }

  return (
    <>
      {generateFields({
        form,
        canModify,
        canSetPermissions,
        shouldValidate: false,
        field: { ...field, name },
        showInactive: !!router.query.archived
      })}
    </>
  )
}

export default FieldContent
